import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import isEmpty from 'lodash/isEmpty';

import { FieldsetForObject, Input, TOOLTIP_MODES } from 'moet-core';
import { BorderlessDropdown } from '@klarna/bubble-ui';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import {
  accountHolder as accountHolderValidator,
  accountNumber as accountNumberValidator,
  bic as bicValidator,
  routingNumber as routingNumberValidator,
} from '@klarna/mf-validations';
import { translator } from 'mage-i18n';

import { validators } from '@/flow/validation';
import { SwitchToggleField } from '@/components';
import {
  getMerchantAwareTranslationKey,
  getMerchantAwareTranslator,
  getMerchantCountryKey,
} from '@/selectors/merchant';

import { CY_BANK_ACCOUNT_CONTAINER_IDS } from '../constants';

import { withValidation } from './withValidation';

const tContainerPrefix = 'products-fe.containers.bank_account';

const findInvalidFieldEntries = fields =>
  Object.entries(fields)
    .filter(([, field]) => Object.prototype.hasOwnProperty.call(field, 'isValid'))
    .filter(([, field]) => !field.isValid);

const LocalFields = props => {
  const {
    t,
    tMerchantAware,
    tKey,
    country,
    currencies,
    currency,
    currencyTooltipText,
    useAsDefaultTooltipText,
    accountHolder,
    accountNumber,
    bic,
    routingNumber,
    isCurrencyDisabled,
    isDisabled,
    useAsDefault,
    onFormValidityChange = validity => {},
    onFieldChange = (field, value) => {},
    onFieldBlur = field => {},
    onFieldFocus = field => {},
    ...rest
  } = props;

  const [touchedFields, setTouchedFields] = useState({
    currency: !isEmpty(currency),
    accountHolder: !isEmpty(accountHolder),
    bic: !isEmpty(bic),
    routingNumber: !isEmpty(routingNumber),
    accountNumber: !isEmpty(accountNumber),
    useAsDefault: !isEmpty(useAsDefault),
  });
  const touchField = key => {
    setTouchedFields(prev => ({
      ...prev,
      [key]: true,
    }));
  };

  const bicField = {
    bic: {
      component: Input,
      type: 'text',
      size: 1,
      ...withValidation(
        {
          label: tMerchantAware(`${tContainerPrefix}.fields.bic.label`),
          value: bic,
          isTouched: touchedFields.bic || '',
          isValid: false,
          validators: [
            {
              callback: bicValidator.validate,
              errorMessage: `${tContainerPrefix}.fields.bic.errors.general`,
              options: { country },
            },
          ],
          extraProps: {
            disabled: isDisabled,
            tooltipText: tMerchantAware(`${tContainerPrefix}.fields.bic.tooltip`),
            ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.INPUT_ROUTING_NUMBER, window),
          },
        },
        tMerchantAware,
        props,
      ),
      sortOrder: 3,
    },
  };

  const routingNumberField = {
    routingNumber: {
      component: Input,
      type: 'text',
      size: 1,
      ...withValidation(
        {
          label: tMerchantAware(`${tContainerPrefix}.fields.routing_number.label`),
          value: routingNumber || '',
          isTouched: touchedFields.routingNumber,
          isValid: false,
          validators: [
            {
              callback: routingNumberValidator.validate,
              errorMessage: tKey(`${tContainerPrefix}.fields.routing_number.errors.general`),
              options: { country },
            },
          ],
          extraProps: {
            disabled: isDisabled,
            tooltipText: tMerchantAware(`${tContainerPrefix}.robot_message_routing_number`),
            ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.INPUT_ROUTING_NUMBER, window),
          },
        },
        t,
        props,
      ),
      sortOrder: 3,
    },
  };

  const specialNumber = {
    AU: { ...bicField },
    CA: { ...routingNumberField },
    GB: { ...routingNumberField },
    SE: { ...routingNumberField },
    US: { ...routingNumberField },
    MX: {},
  };

  const bankAccountForm = {
    currency: {
      component: BorderlessDropdown,
      size: 1,
      ...withValidation(
        {
          label: t(`${tContainerPrefix}.fields.currency_code.label`),
          value: currency,
          isTouched: isCurrencyDisabled || touchedFields.currency,
          isValid: isCurrencyDisabled,
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tContainerPrefix}.fields.currency_code.errors.general`),
            },
          ],
          extraProps: {
            options: currencies,
            autoFocus: !currency,
            disabled: isCurrencyDisabled || isDisabled,
            fieldTooltip: currencyTooltipText,
            ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.INPUT_CURRENCY, window),
          },
        },
        t,
        props,
      ),
      sortOrder: 0,
    },
    accountHolder: {
      component: Input,
      type: 'text',
      size: 1,
      ...withValidation(
        {
          label: t(`${tContainerPrefix}.fields.account_holder.label`),
          value: accountHolder,
          isTouched: touchedFields.accountHolder,
          isValid: false,
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: `${tContainerPrefix}.fields.account_holder.errors.general`,
            },
            {
              callback: accountHolderValidator.validate,
              errorMessage: `${tContainerPrefix}.fields.account_holder.errors.general`,
              options: { country, currency },
            },
          ],
          extraProps: {
            disabled: isDisabled,
            ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.INPUT_ACCOUNT_HOLDER, window),
          },
        },
        t,
        props,
      ),
      sortOrder: 2,
    },
    ...specialNumber[country],
    accountNumber: {
      component: Input,
      type: 'text',
      size: 1,
      ...withValidation(
        {
          label: tMerchantAware(`${tContainerPrefix}.fields.local_account_number.label`),
          value: accountNumber,
          isTouched: touchedFields.accountNumber,
          isValid: false,
          validators: [
            {
              callback: accountNumberValidator.validate,
              errorMessage: `${tContainerPrefix}.fields.local_account_number.errors.general`,
              options: { country },
            },
          ],
          extraProps: {
            disabled: isDisabled,
            tooltipText: tMerchantAware(`${tContainerPrefix}.robot_message_account_number`),
            ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.INPUT_ACCOUNT_NUMBER, window),
          },
        },
        tMerchantAware,
        props,
      ),
      sortOrder: 4,
    },
    useAsDefault: {
      component: SwitchToggleField,
      label: t(`${tContainerPrefix}.fields.use_as_default_bank_account.label`),
      extraProps: {
        checked: useAsDefault,
        disabled: isDisabled,
        ...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.INPUT_USE_AS_DEFAULT, window),
        tooltipText: useAsDefaultTooltipText,
        tooltipMode: TOOLTIP_MODES.ICON,
      },
      sortOrder: 5,
    },
  };

  useEffect(() => {
    const invalidFieldEntries = findInvalidFieldEntries(bankAccountForm);
    const invalidFields = invalidFieldEntries.map(([key, field]) => ({
      key,
      value: field.value,
      isValid: field.isValid,
    }));

    onFormValidityChange({
      isFormValid: !invalidFieldEntries.length,
      invalidFields: invalidFields,
    });
  }, [currency, accountHolder, bic, routingNumber, accountNumber, useAsDefault]);

  const handleFieldBlur = key => {
    touchField(key);
    if (key === 'accountNumber' && country === 'GB') {
      onFieldChange('accountNumber', accountNumber.padStart(8, '0'));
    }
    return onFieldBlur(key);
  };

  const handleFieldFocus = key => {
    touchField(key);
    return onFieldFocus(key);
  };

  const handleFieldChange = (key, val) => {
    return onFieldChange(key, val);
  };

  return (
    <FieldsetForObject
      {...rest}
      isDisabled={isDisabled}
      name="bank-accounts"
      fields={bankAccountForm}
      onBlur={handleFieldBlur}
      onFocus={handleFieldFocus}
      onChange={handleFieldChange}
    />
  );
};

LocalFields.propTypes = {
  accountHolder: PropTypes.string,
  accountNumber: PropTypes.string,
  bic: PropTypes.string,
  country: PropTypes.string,
  currencies: PropTypes.array,
  currency: PropTypes.string,
  currencyTooltipText: PropTypes.string,
  isCurrencyDisabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  routingNumber: PropTypes.string,
  t: PropTypes.func,
  tKey: PropTypes.func,
  tMerchantAware: PropTypes.func,
  useAsDefault: PropTypes.bool,
  useAsDefaultTooltipText: PropTypes.string,
  onFieldBlur: PropTypes.func,
  onFieldChange: PropTypes.func,
  onFieldFocus: PropTypes.func,
  onFormValidityChange: PropTypes.func,
};

export default compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    tKey: getMerchantAwareTranslationKey(state),
    country: getMerchantCountryKey(state),
  })),
)(LocalFields);
